import type { MemberByIdResult } from "./graphql/memberById";

/** Subset of MemberProfilePage's local `PpMemberState` this mapper needs.
 *  Not imported from MemberProfilePage.tsx (a page component, not a shared
 *  types module) — kept as a narrow structural type instead. */
interface PpMemberInput {
  membKeyId?: string | null;
  membMpiNo?: string;
  startDate?: string | null;
}

// Create-Auth-V2 native migration, Phase 4 (ACT-1247). Maps member-360's own
// member record to the shape @astrana/ui's native Create Auth module
// (Authorizations' `authMember` prop / CreateAuth's `AuthMember`) reads.
//
// NOTE: `AuthMember`/`AuthProvider` are NOT part of @astrana/ui's public
// export surface as of feat/create-auth-v2-native@81b8c9fe (only
// `AuthorizationsProps` — which structurally includes `authMember?:
// AuthMember` — is exported; the nominal type itself lives under an internal
// path). This file therefore does NOT import the lib type; the object
// literal below is checked structurally against the `authMember` prop at the
// JSX call site in MemberProfilePage.tsx. If a future @astrana/ui version
// re-exports `AuthMember` from `@astrana/ui/modules`, swap this local
// `NativeAuthMember` alias for the real import (would catch drift for free).
//
// FIELD-BY-FIELD MAPPING + GAPS — cross-checked against what
// useCreateAuthInit.ts / buildPayload.ts / cfcCapOverride.ts actually READ
// off `authMember` (see astrana-ui-modules
// CreateAuth/form/CreateAuthDataContext.tsx's own "PARITY RISK" note):
//
//   CRITICAL (live-read by the form's queries/payload — get these right):
//     RmiCompanyId   <- companyId (already resolved by the host: ipaCode,
//                       undefined when ipaCode === "NA" — same rule as
//                       toPatientObject.companyId). Gates 5+ RTK queries
//                       (POS, refer-type, modifier codes, ExpiryDays,
//                       IpaAdvise...) via `skip: !authMember.RmiCompanyId`.
//     RmiLobCode     <- member.lineOfBusiness. Paired with RmiCompanyId for
//                       the ExpiryDays query (both required or it's skipped).
//     RmiMembKeyId   <- member.memberIdOthers.apolloMemberKey (mirrors
//                       toPatientObject's `mkey` convention). Gates the
//                       member-condition-codes + CheckSpecialistAuthHistory
//                       queries.
//     RmiHpCode      <- member.healthplanCode. Gates MediCalCode lookup +
//                       drives the Health-Net-LOB vendor-provider check.
//     RmiBirth       <- member.dob. Feeds buildPayload's RmiAge
//                       (calculateAge) — wrong/missing value means a wrong
//                       age on the submitted auth, not a query skip.
//     RmiStartDate   <- member.enrollmentDate. Feeds buildPayload's date
//                       field (best-available date; enrollmentDate is NOT
//                       necessarily the eligibility-span start date PP's own
//                       AuthMember API would return — see GAP below).
//     RmiSex         <- member.gender, PASSED THROUGH VERBATIM. GAP: member-
//                       360/Pathways' `gender` field format (e.g. "Male" vs
//                       "M") has not been confirmed against what
//                       buildPayload's RmiGender / PP's downstream API
//                       expects (traditionally a single-char M/F code). Do
//                       not assume this is correct without checking a real
//                       Pathways member payload.
//
//   REQUIRED BY TYPE, NOT READ BY ANY PORTED LOGIC TODAY (confirmed via grep
//   — safe to default, but flag here so the next form-section port doesn't
//   silently inherit empty/placeholder data):
//     RmiCity, RmiCountry, RmiCategory, RmiSpecialGroupId, RmiRegionCode,
//     RmiEndDate, RmiProviderKeyId, Capitated — member-360's MEMBER_BY_ID
//     query does not select any of these (no address, no eligibility-
//     category/region/special-group, no PCP provider key, no capitation
//     flag). Left as empty-string / 0, matching astrana-ui-modules' own
//     `buildPlaceholderAuthMember` fallback defaults exactly, so a section
//     that starts reading one of these degrades no worse than the no-host-
//     data fallback already does.
//
//   AM-5713 FOLLOW-UP (GetAuthMember 504, root-caused 2026-07-21): a prior
//   audit claimed `toAuthMemberFromPathways`/`toAuthMemberFromPp` diverged on
//   `RmiProviderKeyId` (one nullish, one `""`). Re-verified against the
//   actual code: FALSE — both mappers already set `RmiProviderKeyId: ""`
//   identically and always have (single commit, #199). The real gap is a
//   DIFFERENT field: astrana-ui-modules' `AuthMemberResponse` (store/types.ts)
//   declares `RmiProviderKeyId: string` AND a second, separate optional
//   `RmiProvKeyId?: string` ("Alternative field name") — and
//   `useCreateAuthInit.ts`'s hydration query checks `RmiProvKeyId` FIRST,
//   falling through to `RmiProviderKeyId`, then to the zero-GUID literal only
//   if BOTH are nullish (`authMember.RmiProvKeyId ?? authMember.
//   RmiProviderKeyId ?? "00000000-0000-0000-0000-000000000000"`, AM-5713 /
//   PR #366). Several OTHER CreateAuth form sections (ServiceTypeProvider
//   Section.tsx's `MemberPcpKeyId`, CptCodeSection.tsx's `memberPcpKeyId`)
//   read `authMember.RmiProvKeyId` DIRECTLY with a `||` fallback, ignoring
//   `RmiProviderKeyId` entirely. Neither host mapper has ever set
//   `RmiProvKeyId` — it's simply absent (not just falsy) on every
//   member-360-built `AuthMember`. Today this happens to resolve safely
//   (`undefined ?? "" ?? zeroGuid` still lands on `""`), but it's a fragile,
//   accidental safety net one refactor away from regressing (e.g. a future
//   `||` instead of `??`, or a coalescing-order swap, would immediately
//   revert to the zero-GUID that the prod wire capture proved causes a BE
//   504). Setting `RmiProvKeyId: ""` explicitly on both mappers (below)
//   removes the dependency on that ordering entirely and gives every current
//   AND future consumer — regardless of which of the two field names it
//   happens to read — the exact value proven safe by the working
//   Provider-Portal-login wire capture ("").
//
//   OPTIONAL EXTENSIONS (RmiPcpMaintCode — cfcCapOverride.ts's CFC prospect
//   cap-override check):
//     RmiPcpMaintCode <- NOT SOURCED. member-360 doesn't carry a PCP
//     maintenance code anywhere (Care Team tab has a different, unrelated
//     provider shape). Left undefined; cfcCapOverride treats a missing/non-
//     "M" maint code as the (safe) non-override path.
//
//   LOW-CONFIDENCE / UNVERIFIED, not read by ported logic today:
//     RmiMembMpiNo, RmiMpiNo <- member.memberIdOthers.mbi, mirroring
//     toPatientObject's documented `mpi <- .mbi` (not `.mpi`) gotcha. Same
//     caveat applies here if a future section starts reading it.
//     RmiMemberId <- same value as RmiMembId (documented in
//     AuthMemberResponse as "alternative field name used in some contexts").
//     RmiFirstName/RmiLastName/RmiFirstNm/RmiLastNm <- NOT SOURCED.
//     member.name is a single combined display string; naively splitting on
//     whitespace is wrong for a meaningful fraction of real names (suffixes,
//     multi-word last names), so left undefined rather than guessed.
export interface NativeAuthMember {
  RmiMembKeyId: string;
  RmiMembId: string;
  RmiMemberId?: string;
  RmiCompanyId: string;
  RmiMembMpiNo?: string;
  RmiMpiNo?: string;
  RmiFirstName?: string;
  RmiLastName?: string;
  RmiFirstNm?: string;
  RmiLastNm?: string;
  RmiBirth: string;
  RmiSex: string;
  RmiCity: string;
  RmiCountry: string;
  RmiHpCode: string;
  RmiCategory: string;
  RmiSpecialGroupId: string;
  RmiRegionCode: string;
  RmiStartDate: string;
  RmiEndDate: string;
  RmiProviderKeyId: string;
  /** "Alternative field name" for RmiProviderKeyId (astrana-ui-modules'
   *  AuthMemberResponse type) — see the AM-5713 follow-up note above.
   *  useCreateAuthInit.ts's GetAuthMember hydration query — and several
   *  CreateAuth form sections — read THIS field first/only. Always set it
   *  alongside RmiProviderKeyId so neither name is ever left nullish. */
  RmiProvKeyId?: string;
  Capitated: number;
  RmiLobCode?: string;
  RmiPcpMaintCode?: string;
}

/** Pathways path — member loaded via MEMBER_BY_ID (see MemberProfilePage). */
export function toAuthMemberFromPathways(
  member: NonNullable<MemberByIdResult["PathwaysMember"]>,
  companyId: string | undefined,
): NativeAuthMember {
  return {
    RmiMembKeyId: member.memberIdOthers?.apolloMemberKey ?? "",
    RmiMembId: member.memberPolicyId ?? "",
    RmiMemberId: member.memberPolicyId ?? undefined,
    RmiCompanyId: companyId ?? "",
    RmiMembMpiNo: member.memberIdOthers?.mbi ?? undefined,
    RmiMpiNo: member.memberIdOthers?.mbi ?? undefined,
    RmiBirth: member.dob ?? "",
    RmiSex: member.gender ?? "",
    // Not selected by MEMBER_BY_ID — see file header GAP note.
    RmiCity: "",
    RmiCountry: "",
    RmiHpCode: member.healthplanCode ?? "",
    RmiCategory: "",
    RmiSpecialGroupId: "",
    RmiRegionCode: "",
    RmiStartDate: member.enrollmentDate ?? "",
    RmiEndDate: "",
    RmiProviderKeyId: "",
    RmiProvKeyId: "",
    Capitated: 0,
    RmiLobCode: member.lineOfBusiness ?? "",
    RmiPcpMaintCode: undefined,
  };
}

/** PP path — member carried via location.state (no Pathways record at all).
 *  Fewer fields available than the Pathways path (no dob/gender/healthplan/
 *  lineOfBusiness in `PpMemberState`) — degrades further, documented inline. */
export function toAuthMemberFromPp(
  ppMember: PpMemberInput | undefined,
  ppMemberId: string,
  companyId: string | undefined,
): NativeAuthMember {
  return {
    RmiMembKeyId: ppMember?.membKeyId ?? "",
    RmiMembId: ppMemberId,
    RmiMemberId: ppMemberId || undefined,
    RmiCompanyId: companyId ?? "",
    RmiMembMpiNo: ppMember?.membMpiNo ?? undefined,
    RmiMpiNo: ppMember?.membMpiNo ?? undefined,
    // PpMemberState carries no dob/gender/healthplan/lineOfBusiness at all
    // (see MemberProfilePage's LocationState) — GAP, not just unselected.
    RmiBirth: "",
    RmiSex: "",
    RmiCity: "",
    RmiCountry: "",
    RmiHpCode: "",
    RmiCategory: "",
    RmiSpecialGroupId: "",
    RmiRegionCode: "",
    RmiStartDate: ppMember?.startDate ?? "",
    RmiEndDate: "",
    RmiProviderKeyId: "",
    RmiProvKeyId: "",
    Capitated: 0,
    RmiLobCode: "",
    RmiPcpMaintCode: undefined,
  };
}
